import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft,
  CheckCircle,
  CaretRight,
  Eraser,
  PaperPlaneTilt,
  Copy,
  ChatCircleText,
  Note as NoteIcon,
  Clock,
} from "@phosphor-icons/react";
import TasksPanel from "@/components/TasksPanel";

// In-office signature pad (unchanged)
const SignaturePad = ({ onSave }) => {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [signerName, setSignerName] = useState("");

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    canvas.width = canvas.offsetWidth * 2;
    canvas.height = canvas.offsetHeight * 2;
    ctx.scale(2, 2);
    ctx.strokeStyle = "#1A2F24";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
  }, []);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    return { x, y };
  };
  const start = (e) => {
    e.preventDefault();
    setDrawing(true);
    const { x, y } = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const { x, y } = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
  };
  const end = () => setDrawing(false);
  const clear = () => {
    const canvas = canvasRef.current;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
  };
  const save = () => {
    if (!signerName.trim()) {
      toast.error("Please enter the signer's printed name.");
      return;
    }
    onSave(canvasRef.current.toDataURL("image/png"), signerName);
  };

  return (
    <div className="space-y-3">
      <p className="label-eyebrow">In-office signature (client present)</p>
      <canvas
        ref={canvasRef}
        className="signature-pad"
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
        data-testid="signature-canvas"
      />
      <div className="grid md:grid-cols-[1fr_auto_auto] gap-2 items-end">
        <div>
          <label className="label-eyebrow block mb-2">Printed name</label>
          <input
            data-testid="signer-name"
            className="input-clean"
            value={signerName}
            onChange={(e) => setSignerName(e.target.value)}
          />
        </div>
        <button onClick={clear} className="btn-outline" data-testid="signature-clear">
          <Eraser size={14} className="inline mr-2" /> Clear
        </button>
        <button onClick={save} className="btn-primary" data-testid="signature-save">
          Sign In-Office
        </button>
      </div>
    </div>
  );
};

const ACTIVITY_ICON = {
  engagement_created: NoteIcon,
  step_advanced: CaretRight,
  letter_sent: PaperPlaneTilt,
  letter_viewed: ChatCircleText,
  letter_signed: CheckCircle,
  document_uploaded: NoteIcon,
  note: NoteIcon,
  message_to_client: ChatCircleText,
  message_from_client: ChatCircleText,
  client_chat_started: ChatCircleText,
  status_changed: Clock,
};

const EngagementDetailPage = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const [eng, setEng] = useState(null);
  const [steps, setSteps] = useState([]);
  const [activity, setActivity] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [signMode, setSignMode] = useState(false);
  const [letterUrl, setLetterUrl] = useState(null);

  const load = async () => {
    const [e1, list, act] = await Promise.all([
      api.get(`/engagements/${id}`),
      api.get("/engagements"),
      api.get(`/engagements/${id}/activity`),
    ]);
    setEng(e1.data);
    setSteps(list.data.workflow_steps);
    setActivity(act.data.items);
    if (e1.data.letter_token) {
      setLetterUrl(`${window.location.origin}/sign/${e1.data.letter_token}`);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [id]);

  if (!eng) return <div className="label-eyebrow">Loading…</div>;

  const advance = async () => {
    if (eng.current_step >= steps.length - 1) {
      toast.info("Already at final step.");
      return;
    }
    const r = await api.patch(`/engagements/${id}`, { step: eng.current_step + 1 });
    setEng(r.data);
    load();
    toast.success("Step advanced.");
  };

  const sendLetter = async () => {
    try {
      const r = await api.post(`/engagements/${id}/letter/send`);
      setLetterUrl(r.data.url);
      navigator.clipboard.writeText(r.data.url);
      toast.success("Signing link generated & copied to clipboard.");
      load();
    } catch {
      toast.error("Could not generate signing link.");
    }
  };

  const signInOffice = async (signature_data_url, signer_name) => {
    try {
      const r = await api.post(`/engagements/${id}/sign`, { signature_data_url, signer_name });
      setEng(r.data);
      setSignMode(false);
      load();
      toast.success("Signed in-office.");
    } catch {
      toast.error("Could not save signature.");
    }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    await api.post(`/engagements/${id}/notes`, { text: noteText });
    setNoteText("");
    load();
  };

  const openClientChat = async () => {
    try {
      const r = await api.post("/threads", {
        kind: "client",
        engagement_id: id,
        initial_message: `Hi ${eng.client_name} — happy to chat about your ${eng.service_type}.`,
      });
      nav("/messages");
      toast.success("Client chat opened — copy the link from the Messages page.");
      return r;
    } catch {
      toast.error("Could not open client chat.");
    }
  };

  const pct = (eng.current_step / Math.max(1, steps.length - 1)) * 100;

  return (
    <div data-testid="engagement-detail-page" className="space-y-8">
      <Link
        to="/engagements"
        className="label-eyebrow inline-flex items-center gap-2 hover:text-[#1A2F24]"
      >
        <ArrowLeft size={12} /> Engagements
      </Link>

      <div className="pb-8 border-b border-[#1A2F24]/12">
        <p className="label-eyebrow">{eng.service_type}</p>
        <h1 className="font-serif text-5xl text-[#112018] mt-2">{eng.client_name}</h1>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-6">
          <Field label="Client email" value={eng.client_email} />
          <Field label="Fee" value={`$${eng.fee.toLocaleString()}`} mono />
          <Field label="Status" value={eng.status} />
          <Field
            label="Created"
            value={new Date(eng.created_at).toLocaleDateString()}
          />
        </div>
      </div>

      {/* Progress + workflow */}
      <div className="card-flat">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="label-eyebrow">Workflow Progress</p>
            <h2 className="font-serif text-2xl text-[#112018] mt-1">
              Step {eng.current_step + 1} of {steps.length} ·{" "}
              <em className="not-italic text-[#A8977B]">
                {steps[eng.current_step]?.label}
              </em>
            </h2>
          </div>
          <button
            onClick={advance}
            className="btn-primary"
            data-testid="advance-step-btn"
            disabled={eng.current_step >= steps.length - 1}
          >
            Advance Step <CaretRight size={14} className="inline ml-1" />
          </button>
        </div>
        <div className="progress-track mb-6">
          <div className="progress-fill" style={{ width: `${pct}%` }} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-7 gap-2">
          {steps.map((s, i) => (
            <div
              key={s.key}
              className={`p-3 border text-center ${
                i <= eng.current_step
                  ? "border-[#1A2F24] bg-[#1A2F24] text-[#F7F6F2]"
                  : "border-[#1A2F24]/12 bg-white text-[#606C64]"
              }`}
              data-testid={`workflow-step-${s.key}`}
            >
              <p className="font-mono text-[10px] tracking-widest">STEP {i + 1}</p>
              <p className="text-xs mt-2">{s.label}</p>
              {i <= eng.current_step && <CheckCircle size={14} className="inline mt-1" />}
            </div>
          ))}
        </div>
      </div>

      {/* Tasks panel */}
      <TasksPanel engagementId={id} engagementOwnerId={eng.owner_id} />

      {/* Letter + signing */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="card-flat">
          <p className="label-eyebrow">Engagement Letter</p>
          <h2 className="font-serif text-2xl text-[#112018] mt-1">Send & Sign</h2>
          <div className="mt-4 p-4 bg-[#F7F6F2] border border-[#1A2F24]/12 text-sm text-[#1A2F24] leading-relaxed">
            <p>
              <strong>{eng.client_name}</strong>,
            </p>
            <p className="mt-3">
              This letter confirms our engagement to provide <em>{eng.service_type}</em>{" "}
              services for a fixed fee of{" "}
              <span className="font-mono">${eng.fee.toLocaleString()}</span>.
            </p>
            {eng.description && <p className="mt-3 italic">{eng.description}</p>}
          </div>

          {eng.signature_data_url ? (
            <div className="mt-6" data-testid="letter-signed">
              <p className="label-eyebrow">
                Signed via {eng.signed_method === "client_link" ? "client link" : "in-office"}
              </p>
              <img
                src={eng.signature_data_url}
                alt="signature"
                className="mt-3 border border-[#1A2F24]/20 bg-white max-h-32"
              />
              <p className="text-sm mt-3 text-[#1A2F24]">
                <strong>{eng.signer_name}</strong> ·{" "}
                <span className="font-mono text-xs">
                  {new Date(eng.signed_at).toLocaleString()}
                </span>
              </p>
              {eng.signed_audit?.ip && (
                <p className="label-eyebrow mt-2">
                  IP {eng.signed_audit.ip} · UA recorded
                </p>
              )}
            </div>
          ) : (
            <div className="mt-6 space-y-4">
              {/* Send to client */}
              <div className="border border-[#1A2F24]/12 p-4">
                <p className="label-eyebrow">Recommended</p>
                <p className="text-sm text-[#1A2F24] mt-1 mb-3">
                  Send a secure signing link to {eng.client_email}. The client can review
                  and sign from any device. Each signature captures IP + timestamp.
                </p>
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={sendLetter}
                    className="btn-primary"
                    data-testid="send-letter-btn"
                  >
                    <PaperPlaneTilt size={14} className="inline mr-2" />
                    {letterUrl ? "Regenerate Link" : "Send Letter to Client"}
                  </button>
                  {letterUrl && (
                    <>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(letterUrl);
                          toast.success("Link copied.");
                        }}
                        className="btn-outline"
                        data-testid="copy-letter-link"
                      >
                        <Copy size={12} className="inline mr-2" /> Copy
                      </button>
                      <a
                        href={letterUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="btn-outline"
                        data-testid="open-letter-preview"
                      >
                        Open Preview
                      </a>
                    </>
                  )}
                </div>
                {letterUrl && (
                  <p className="font-mono text-[11px] text-[#606C64] mt-3 break-all">
                    {letterUrl}
                  </p>
                )}
                {eng.letter_sent_at && (
                  <p className="label-eyebrow mt-3">
                    Link generated {new Date(eng.letter_sent_at).toLocaleString()}
                    {eng.letter_viewed_at && (
                      <> · Opened by client {new Date(eng.letter_viewed_at).toLocaleString()}</>
                    )}
                  </p>
                )}
              </div>

              {/* In-office signing */}
              <div className="border border-[#1A2F24]/12 p-4">
                <p className="label-eyebrow">Alternative</p>
                <p className="text-sm text-[#1A2F24] mt-1 mb-3">
                  Capture the signature in person.
                </p>
                {signMode ? (
                  <SignaturePad onSave={signInOffice} />
                ) : (
                  <button
                    onClick={() => setSignMode(true)}
                    className="btn-outline"
                    data-testid="open-in-office-btn"
                  >
                    Sign In-Office
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Activity feed + notes */}
        <div className="card-flat">
          <div className="flex items-center justify-between mb-1">
            <p className="label-eyebrow">Activity & Notes</p>
            <button
              onClick={openClientChat}
              className="btn-outline"
              data-testid="open-client-chat-btn"
            >
              <ChatCircleText size={12} className="inline mr-2" /> Client Chat
            </button>
          </div>
          <h2 className="font-serif text-2xl text-[#112018]">Engagement Feed</h2>

          {/* Note composer */}
          <div className="mt-4 flex gap-2">
            <input
              data-testid="note-input"
              className="input-clean flex-1"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addNote()}
              placeholder="Add an internal note…"
            />
            <button
              onClick={addNote}
              className="btn-primary"
              data-testid="add-note-btn"
            >
              Note
            </button>
          </div>

          {/* Feed */}
          <div
            className="mt-5 space-y-3 max-h-[440px] overflow-y-auto pr-2"
            data-testid="activity-feed"
          >
            {activity.length === 0 ? (
              <p className="text-sm text-[#606C64] italic py-8 text-center">
                Activity will appear here as the engagement progresses.
              </p>
            ) : (
              activity.map((a) => {
                const Icon = ACTIVITY_ICON[a.type] || NoteIcon;
                return (
                  <div
                    key={a.id}
                    className="flex gap-3 border-l-2 border-[#A8977B] pl-3 py-1"
                    data-testid={`activity-${a.id}`}
                  >
                    <Icon size={16} className="text-[#A8977B] mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm text-[#1A2F24]">{a.text}</p>
                      <p className="label-eyebrow mt-1">
                        {a.by} · {new Date(a.at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, value, mono = false }) => (
  <div>
    <p className="label-eyebrow">{label}</p>
    <p className={`mt-1 ${mono ? "font-mono" : ""} text-[#112018]`}>{value}</p>
  </div>
);

export default EngagementDetailPage;
